import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { LayerApi } from '../../../api/layer-api';
import { IMCMapView } from '../../../classes/mcMapView';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter, NumberConverter } from '../../../inputConverter';
import { baseScaleIntervals } from '../../../selectionOptions';
import { LayerService } from '../../../services/layer.service';
import { ILayerColumn } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-layer-labels',
    styleUrls: ['../../sharedStyles/base.scss', './layer-labels.component.scss'],
    templateUrl: './layer-labels.component.html'
})
export class LayerLabelsComponent extends BaseComponent implements AfterViewInit, OnChanges, OnInit {

    esriLayer: any;
    showLoading: boolean = false;
    @Input() mapViewRef: IMCMapView;
    @Input() inputLayerApi: LayerApi;

    _layerId: number;
    @Input()
    @InputConverter(NumberConverter)
    get layerId() { return this._layerId; }
    set layerId(layerId: number) { this.setLayer(layerId); }

    private _uniqueLayerId;
    @Input() set uniqueLayerId(value) {
        this._uniqueLayerId = value;
        if (value) {
            this.esriLayer = this.layerService.getEsriLayerByUniqueId(this._uniqueLayerId);
            this.layerId = this.esriLayer.mapcoreProps.parentId;
        }
    }
    get uniqueLayerId() {
        return this._uniqueLayerId;
    }

    @Input() layerColumns: ILayerColumn[] = [];
    @Input() layerSymbol: NonNullable<__esri.Symbol>;
    @Input() labelClass: NonNullable<__esri.LabelClass>;
    @Input() selectedLayerColumn: ILayerColumn;
    @Input() textSymbolEditorRef: any;

    startScaleIntervals = [
        {
            label: 'None',
            value: 0
        },
        ...baseScaleIntervals
    ];
    stopScaleIntervals = [
        {
            label: 'None',
            value: 0
        },
        ...baseScaleIntervals
    ];

    placements = [
        { name: 'Always Horizontal', value: 'always-horizontal' },
        { name: 'Above Center', value: 'above-center' },
        { name: 'Above Left', value: 'above-left' },
        { name: 'Above Right', value: 'above-right' },
        { name: 'Above After', value: 'above-after' },
        { name: 'Above Along', value: 'above-along' },
        { name: 'Above Before', value: 'above-before' },
        { name: 'Above Start', value: 'above-start' },
        { name: 'Above End', value: 'above-end' },
        { name: 'Below Center', value: 'below-center' },
        { name: 'Below Left', value: 'below-left' },
        { name: 'Below Right', value: 'below-right' },
        { name: 'Below After', value: 'below-after' },
        { name: 'Below Along', value: 'below-along' },
        { name: 'Below Before', value: 'below-before' },
        { name: 'Below Start', value: 'below-start' },
        { name: 'Below End', value: 'below-end' },
        { name: 'Center Center', value: 'center-center' },
        { name: 'Center Left', value: 'center-left' },
        { name: 'Center Right', value: 'center-right' },
        { name: 'Center After', value: 'center-after' },
        { name: 'Center Along', value: 'center-along' },
        { name: 'Center Before', value: 'center-before' },
        { name: 'Center Start', value: 'center-start' },
        { name: 'Center End', value: 'center-end' }
    ];

    includeHalo = false;
    haloOptions = {
        color: 'red',
        size: 3
    };

    @Input() get textSymbolJSON(): any {
        this.textSymbolEditorRef.applyChange();
        const textSymbol = this.layerSymbol;
        const textSymbolJSON = textSymbol.toJSON();
        textSymbolJSON.type = 'text';
        return textSymbolJSON;
    }

    @Input() get labelClassDefinition(): any {
        this.textSymbolEditorRef.applyChange();
        const columnName = this.selectedLayerColumn.name;
        this.labelClass.labelExpression = `[${columnName}]`;
        if (this.includeHalo) {
            (<__esri.TextSymbol>this.labelClass.symbol).haloSize = this.haloOptions.size;
            (<__esri.TextSymbol>this.labelClass.symbol).haloColor = this.haloOptions.color as any;
        }
        const labelClassDefinition = this.labelClass.toJSON();
        labelClassDefinition.labelExpressionInfo = null;
        labelClassDefinition.labelPlacement = this.labelClass.labelPlacement;
        labelClassDefinition.symbol = this.textSymbolJSON;
        return labelClassDefinition;
    }

    @Output() public readonly onclose = new EventEmitter<boolean>();

    @ViewChild('textSymbolEditor') set textSymbolEditor(textSymbolEditor: ElementRef<any>) {
        this.textSymbolEditorRef = textSymbolEditor;
    }

    constructor(_vcr: ViewContainerRef, private readonly cdr: ChangeDetectorRef, private readonly layerApi: LayerApi, private readonly layerService: LayerService) {
        super(_vcr, true);
        this.registerEvent('onclose', this.onclose);
        this.inputLayerApi = layerApi;
    }

    colorPicker: ViewContainerRef;
    @ViewChild('colorPicker') set colorPickerRef(el: ViewContainerRef) { this.colorPicker = el; }

    async apply() {
        this.showLoading = true;
        await this.setLabelingInfoForLayer(true);
        this.showLoading = false;
        this.layerService.layersRefreshed.emit();
    }

    async remove() {
        await this.setLabelingInfoForLayer(false);
    }

    async setLabelingInfoForLayer(apply: boolean) {
        if (this.esriLayer.sublayers) {

            const watch = this.mapViewRef.watch('updating', (newValue) => {
                this.showLoading = newValue;
                this.cdr.detectChanges();
                if (!newValue) {
                    watch.remove();
                }
            });

            for (const sublayer of this.esriLayer.sublayers.items) {
                if (!apply && sublayer.labelingInfo) {
                    this.esriLayer.mapcoreProps.labelingInfo = undefined;
                    sublayer.labelingInfo.length = 0;
                    delete sublayer.labelingInfo;
                } else if (apply) {
                    this.esriLayer.mapcoreProps.labelingInfo = [this.labelClassDefinition];
                    sublayer.labelingInfo = [this.labelClassDefinition];
                }
            }
            this.esriLayer.refresh();
            this.onclose.emit(true);
        }
    }

    async ngAfterViewInit() {
        this.mapViewRef = await this.setupMapViewRef(this.mapViewRef);
    }

    ngOnChanges() {
        if (this.labelClass && this.labelClass.symbol) {
            this.layerSymbol = this.labelClass.symbol;
        }
        this.setLayer(this.layerId);
    }

    async ngOnInit() {
        this.labelClass = await this.getDefaultLabelClass();
        this.layerSymbol = this.labelClass.symbol;
    }

    clearLayerSettings() {
        this.layerId = null;
        this.layerColumns = null;
        this.selectedLayerColumn = null;
    }

    async setLayer(layerId: number) {
        this._layerId = layerId;
        if (this.labelClass && this.labelClass.symbol) {
            this.layerSymbol = this.labelClass.symbol;
        }
        if (layerId) {
            const layers = await this.layerApi.getLayers(
                [this.layerId],
                'columns{alias, name}');
            if (layers.length > 0) {
                this.layerColumns = (layers[0].columns as ILayerColumn[]).sort((a, b) => {
                    if (a.alias.toLowerCase() > b.alias.toLowerCase()) {
                        return 1;
                    } else if (a.alias.toLowerCase() < b.alias.toLowerCase()) {
                        return -1;
                    }
                    return 0;
                });
                this.selectedLayerColumn = this.layerColumns[0];
            } else {
                this.clearLayerSettings();
            }
        } else {
            this.clearLayerSettings();
        }
    }

    async getDefaultLabelClass() {
        const [Color, TextSymbol, Font, LabelClass] =
            await ESRI.loadModules('esri/Color', 'esri/symbols/TextSymbol', 'esri/symbols/Font', 'esri/layers/support/LabelClass');
        const symbolColor: __esri.Color = new Color();
        symbolColor.setColor('#FF0000');
        const textSymbol: __esri.TextSymbol = new TextSymbol();
        textSymbol.color = symbolColor;
        textSymbol.text = '';
        textSymbol.verticalAlignment = 'middle';
        const font: __esri.Font = new Font();
        font.family = 'arial';
        const fontsize = 12;
        font.size = fontsize;
        font.weight = 'bold';
        textSymbol.font = font;
        const labelClass: __esri.LabelClass = new LabelClass();
        labelClass.labelPlacement = 'always-horizontal';
        labelClass.symbol = textSymbol;
        return labelClass;
    }

    updateColorFromPicker(evt) {
        this.haloOptions.color = evt;
    }
}
